export type Lang = "en";

/**
 * H2's copy.
 *
 * 🔴 THE HARD SENTENCE IS THE ONE ABOUT ANONYMITY, AND IT HAS TO BE TRUE.
 *
 * `model Feedback` stores a rating, an optional score, an optional comment and a
 * timestamp — no parent, no child, no school, no IP. So the screen may say the
 * survey is anonymous, and that is unusually load-bearing: a parent decides how
 * candid to be on the strength of it. The day anyone adds a `schoolId` or a
 * `userId` to that model, this string becomes a lie told to the person it is
 * about, and the copy must change in the same commit.
 *
 * ⚠️ It also must not overclaim. It does NOT say "we cannot tell who you are" —
 * the request still has an IP at the edge and a rate limiter keyed on it. It
 * says what is true: nothing identifying is *stored with the answer*.
 */
export const FEEDBACK_COPY = {
  en: {
    title: "How are we doing?",
    intro: "A few seconds, and it shapes what we build next.",
    ratingLabel: "How is Kalqy working for your family?",
    ratingLow: "Not working",
    ratingHigh: "Working well",
    npsLabel: "Would you recommend Kalqy to another parent?",
    npsLow: "Not likely",
    npsHigh: "Very likely",
    npsOptional: "You can skip this.",
    commentLabel: "Anything you would like to tell us?",
    commentPlaceholder: "What is working, what is not.",
    anonymous:
      "Answers are stored without your name, your number, or your child.",
    submit: "Send",
    sending: "Sending…",
    thanks: "Thank you — this goes straight to the people building it.",
    close: "Close",
    needRating: "Please choose a rating first.",
    tooLong: "That is a little too long. Please shorten it.",
    /** 🔴 A refusal a parent can act on. The server's throttle is 5/minute. */
    tooMany:
      "You have sent this a few times just now. Please try again in a minute.",
    failed: "That did not send. Please try again.",
    charactersLeft: "{n} characters left",
  },
} as const;

export type FeedbackCopyKey = keyof (typeof FEEDBACK_COPY)["en"];

/**
 * The failure a parent sees.
 *
 * 🔴 429 GETS ITS OWN SENTENCE. Every other error here is "try again", which is
 * advice that does not work for a rate limit — the parent retries immediately,
 * is refused again, and concludes the app is broken. The throttle on this route
 * is deliberately tight (5/minute, matching login) because it is the only
 * unauthenticated write that stores free text, so a parent editing and
 * resubmitting can genuinely reach it.
 */
export function describeFeedbackError(err: unknown, lang: Lang): string {
  const copy = FEEDBACK_COPY[lang];
  const status = (err as { status?: number } | null)?.status;
  if (status === 429) return copy.tooMany;
  if (status === 400) return copy.tooLong;
  return copy.failed;
}

/** `{n}` is the only interpolation in this file; kept explicit rather than generic. */
export function charactersLeftText(n: number, lang: Lang): string {
  return FEEDBACK_COPY[lang].charactersLeft.replace("{n}", String(n));
}
